import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import useCtrlZEffect from "./commandZComponent";
import useCtrlShiftZEffect from "./commandShiftZComponent";
import GamePreviewToolbar from "./toolbarGamePreview";

import { previewsGenerated } from "../generated/games.generated";
import { previewSourceFor } from "../modules/previewSource";
import { getServerUri } from "../modules/uriUtility";
import { isVersionedPlayEngineBase } from "../modules/baseKind";
import {
  DEFAULT_DEVICE_ID,
  DEVICE_PRESETS,
  STAGE_INSET,
  ZOOM_FIT,
  ZOOM_STEPS,
  clampZoom,
  devicePresetById,
  orientedDeviceSize
} from "../modules/previewFrame";
import { CONTROL_BORDER, PANEL_BG } from "./snapshotEditor/panelPalette";

import _ from "lodash";
import ReactGA from "react-ga";
import ReactGA4 from "react-ga4";
import { makeStyles } from "@material-ui/core/styles";

import { Typography } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { UndoContext } from "../contexts/UndoContext";

// ── @server: tag hydration utilities ──────────────────────────────────────────

function isServerTag(value) {
  return typeof value === "string" && value.startsWith("@server:");
}

function parseServerTag(value) {
  return value.substring(8); // "@server:".length === 8
}

/**
 * Resolves all @server:<mongoId> tags in a snapshot's asset sources to
 * actual base64 data-URLs by looking up media records and fetching files.
 *
 * Supports both formats:
 *  - Legacy: sources live in snapshot.assets[] (global array)
 *  - New:    sources live on each component dependency (snapshot.components[].dependencies[])
 *
 * Handles:
 *  - string sources (image/audio/video/font): "@server:<mongoId>"
 *  - object sources (atlas): { image: "@server:<imgId>", json: "@server:<jsonId>" }
 *  - object sources (bitmap-font): { image: "@server:<imgId>", xml: "@server:<xmlId>" }
 *  - object sources (spine): { image: "@server:<imgId>", atlas: "@server:<atlasId>", json: "@server:<jsonId>" }
 *
 * @param {object} snapshot - The playEngine snapshot to hydrate
 * @param {object} allMedia - { images, atlases, spines, bitmapFonts, sounds, fonts, videos } arrays
 */
export async function hydrateAssetSources(snapshot, allMedia) {
  if (!snapshot) return snapshot;

  const typeToCollection = {
    image: (allMedia && allMedia.images) || [],
    atlas: (allMedia && allMedia.atlases) || [],
    spine: (allMedia && allMedia.spines) || [],
    "bitmap-font": (allMedia && allMedia.bitmapFonts) || [],
    audio: (allMedia && allMedia.sounds) || [],
    font: (allMedia && allMedia.fonts) || [],
    video: (allMedia && allMedia.videos) || [],
  };

  const typeToFolder = {
    image: "images",
    atlas: "atlases",
    spine: "spines",
    "bitmap-font": "bitmapFonts",
    audio: "sounds",
    font: "fonts",
    video: "videos",
  };

  let spineRefreshPromise;

  async function findRecord(mongoId, assetType) {
    const collection = typeToCollection[assetType] || [];
    const record = collection.find((r) => String(r._id) === mongoId);
    if (record || assetType !== "spine") return record || null;

    // Export can create Spine media after Home fetched its media lists. Refresh
    // once so the newly created @server: IDs are available to this preview.
    if (!spineRefreshPromise) {
      spineRefreshPromise = fetch(getServerUri("spine/find"), {
        method: "POST",
        body: new URLSearchParams({ accessToken: localStorage.getItem("session.accessToken") }),
      }).then((response) => {
        if (!response.ok) throw new Error(`Spine media lookup failed (${response.status})`);
        return response.json();
      });
    }
    const refreshedSpines = await spineRefreshPromise;
    return refreshedSpines.find((r) => String(r._id) === mongoId) || null;
  }

  async function resolveTag(tag, assetType) {
    const mongoId = parseServerTag(tag);
    const record = await findRecord(mongoId, assetType);
    if (!record?.file) {
      console.warn(`[hydrateAssetSources] Missing ${assetType} media record for ${tag}`);
      return null;
    }
    const folder = typeToFolder[assetType] || "images";
    const resp = await fetch(`${folder}/${record.file}`);
    if (!resp.ok) throw new Error(`Failed to load ${assetType} file ${record.file} (${resp.status})`);
    const blob = await resp.blob();
    // Express adds charset=UTF-8 to text responses. FileReader preserves that
    // parameter, but SpineLoader expects the canonical text/plain data URL.
    // Keep the MIME type (including JSON vs ZIP), only remove HTTP parameters.
    const sourceBlob = assetType === "spine"
      ? blob.slice(0, blob.size, blob.type.split(";")[0].trim())
      : blob;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(sourceBlob);
    });
  }

  /**
   * Resolves a single source value (string or atlas object).
   * Returns the hydrated source or original value if no @server: tags found.
   */
  async function resolveSource(source, assetType) {
    if (!source) return source;

    // Simple string tag: "@server:<mongoId>"
    if (isServerTag(source)) {
      try {
        const base64 = await resolveTag(source, assetType);
        return base64 || null;
      } catch (err) {
        console.warn("[hydrateAssetSources] Failed to resolve @server tag:", err);
        return null;
      }
    }

    // Grouped tags: atlas, bitmap font, or Spine's image + atlas + JSON.
    if (typeof source === "object" && source !== null) {
      const newSource = { ...source };
      const parts = assetType === "spine" ? ["image", "atlas", "json"]
        : ["image", "xml" in source ? "xml" : "json"];
      for (const key of parts) {
        if (!isServerTag(source[key])) continue;
        try {
          let base64 = await resolveTag(source[key], assetType);
          if (assetType === "atlas" && key === "json" && base64 && !base64.startsWith("data:application/json;base64,")) {
            const commaIdx = base64.indexOf(",");
            if (commaIdx !== -1) base64 = `data:application/json;base64,${base64.slice(commaIdx + 1)}`;
          }
          newSource[key] = base64 !== null ? base64 : null;
        } catch (err) {
          console.warn(`[hydrateAssetSources] Failed to resolve .${key}:`, err);
          newSource[key] = null;
        }
      }
      return newSource;
    }

    return source; // already a base64 data URL or other non-tag value
  }

  const hydrated = JSON.parse(JSON.stringify(snapshot)); // deep clone

  // ── Legacy format: hydrate snapshot.assets[] (array) ────────────────────────
  if (Array.isArray(hydrated.assets) && hydrated.assets.length > 0) {
    await Promise.all(
      hydrated.assets.map(async (asset, i) => {
        if (!asset.source) return;
        const assetType = (asset.assetId || "").split(":")[0];
        hydrated.assets[i] = {
          ...asset,
          source: await resolveSource(asset.source, assetType),
        };
      })
    );
  }

  // ── Asset-map format: hydrate the keyed assets{} map ────────────────────────
  // Each entry's @server: source is resolved to base64 here; components and
  // localization overrides reference these entries by key (nothing to hydrate
  // on the dependency itself).
  if (
    hydrated.assets &&
    !Array.isArray(hydrated.assets) &&
    typeof hydrated.assets === "object"
  ) {
    await Promise.all(
      Object.keys(hydrated.assets).map(async (assetKey) => {
        const entry = hydrated.assets[assetKey];
        if (!entry || !entry.source) return;
        hydrated.assets[assetKey] = {
          ...entry,
          source: await resolveSource(entry.source, entry.type || "image"),
        };
      })
    );
  }

  // ── New dep-centric format: hydrate component dependencies ──────────────────
  if (Array.isArray(hydrated.components)) {
    await Promise.all(
      hydrated.components.flatMap((comp, ci) =>
        (comp.dependencies || []).map(async (dep, di) => {
          if (!dep.source) return;
          const assetType = dep.type || "image";
          hydrated.components[ci].dependencies[di] = {
            ...dep,
            source: await resolveSource(dep.source, assetType),
          };
        })
      )
    );
  }

  // ── Hydrate localization dependency overrides (enriched format) ──────────────
  const overrides = hydrated.localization?.overrides;
  if (overrides) {
    const promises = [];
    for (const [lang, langOverrides] of Object.entries(overrides)) {
      if (!langOverrides) continue;
      for (const [compId, compOverride] of Object.entries(langOverrides)) {
        if (!compOverride?.dependencyOverrides) continue;
        for (const [depKey, entry] of Object.entries(compOverride.dependencyOverrides)) {
          if (typeof entry !== "object" || !entry || !entry.source) continue;
          if (!isServerTag(entry.source) && !(typeof entry.source === "object")) continue;
          promises.push(
            resolveSource(entry.source, entry.type || "image").then((resolved) => {
              overrides[lang][compId].dependencyOverrides[depKey] = {
                ...entry,
                source: resolved,
              };
            })
          );
        }
      }
    }
    await Promise.all(promises);
  }

  // ── Hydrate engine sounds (top-level snapshot.sounds) ──────────────────────
  if (hydrated.sounds && typeof hydrated.sounds === "object") {
    await Promise.all(
      Object.keys(hydrated.sounds).map(async (key) => {
        const entry = hydrated.sounds[key];
        if (!entry || !entry.source) return;
        hydrated.sounds[key] = {
          ...entry,
          source: await resolveSource(entry.source, "audio"),
        };
      })
    );
  }

  return hydrated;
}

// ── end hydration utilities ───────────────────────────────────────────────────

const useClass = makeStyles({
  gamePreviewDiv: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    // Without these the column cannot shrink below its content, so the stage
    // below never gets a bounded box to fit the frame into.
    minWidth: 0,
    minHeight: 0
  },
  iframeDiv: {
    flex: 1,
    display: "flex",
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px",
    // Room for the handles that sit 20px outside the iframe wrapper.
    paddingBottom: "30px",
    paddingRight: "30px",
    minWidth: 0,
    minHeight: 0,
    // Fit mode never overflows, but a zoom the user picked deliberately may —
    // scrolling it is honest, and it keeps the frame off the neighbouring panes.
    overflow: "auto",
    // A surface for the frame to sit on. Without it a playable whose own
    // background is dark bleeds into the panel and the device edge is guesswork.
    backgroundColor: PANEL_BG
  },
  buttons: {
    zIndex: "100",
    position: "absolute",
    bottom: "55px",
    right: "405px",
    display: "flex",
    backgroundColor: "#30302f",
    borderRadius: "5px",
    flexDirection: "column"
  },
  button: {
    // backgroundColor:"#30302f",
    marginBottom: "10px",
    borderRadius: "5px",
    padding: "10px"
  },
  selectedVersionTitle: { height: "50px", textAlign: "center" },
  gamePreviewIframe: { maxWidth: "100%", maxHeight: "100%", borderStyle: "none" },
  fullScreen: {
    zIndex: 10000,
    position: "absolute",
    width: "100%",
    height: "100%",
    border: "0 !important",
    margin: "0 !important",
    padding: "0 !important",
    top: 0,
    right: 0
  },
  // Occupies the frame's *scaled* size, so the stage centres against what is
  // actually on screen. The handles hang off this rather than off the scaled
  // frame, which keeps them the same size at every scale.
  zoomBox: {
    position: "relative",
    flexShrink: 0,
    // Centres like the stage's own alignment would, but stays scrollable when a
    // hand-picked zoom overflows: `justify-content: center` puts the overflowing
    // half past the scroll origin, where it cannot be reached.
    margin: "auto",
    // Marks exactly where the device ends, so content running to (or past) the
    // edge is visible instead of blending into the stage. Deliberately a ring
    // rather than a border: box-shadow paints outside the box and leaves
    // offsetWidth/offsetHeight alone, which the resize maths reads.
    boxShadow: `0 0 0 1px ${CONTROL_BORDER}, 0 2px 12px rgba(0, 0, 0, 0.45)`
  },
  // The device box at 1:1, scaled down into the zoom box above it.
  iframeWrapper: {
    position: "absolute",
    top: 0,
    left: 0,
    transformOrigin: "top left"
  },
  resizeHandleRight: {
    position: "absolute",
    top: 0,
    width: 14,
    cursor: "ew-resize",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    "& > div": {
      width: 4,
      height: "40%",
      minHeight: 24,
      background: "#595959",
      borderRadius: 3,
      transition: "background 0.15s, height 0.15s"
    },
    "&:hover > div": {
      background: "#4eabb1",
      height: "55%"
    }
  },
  resizeHandleBottom: {
    position: "absolute",
    left: 0,
    height: 14,
    cursor: "ns-resize",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    "& > div": {
      height: 4,
      width: "40%",
      minWidth: 24,
      background: "#595959",
      borderRadius: 3,
      transition: "background 0.15s, width 0.15s"
    },
    "&:hover > div": {
      background: "#4eabb1",
      width: "55%"
    }
  },
  resizeHandleCorner: {
    position: "absolute",
    width: 20,
    height: 20,
    cursor: "nwse-resize",
    zIndex: 11,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#595959",
    transition: "color 0.15s",
    "&:hover": {
      color: "#4eabb1"
    }
  },
  dragSizeOverlay: {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    background: "rgba(0,0,0,0.75)",
    color: "#fff",
    padding: "6px 14px",
    borderRadius: 4,
    fontSize: 13,
    fontFamily: "monospace",
    pointerEvents: "none",
    zIndex: 20,
    letterSpacing: "0.5px",
    whiteSpace: "nowrap",
    userSelect: "none"
  },
  gamePreviewLoader: {
    position: "fixed",
    top: "50%",
    textAlign: "center",
    transform: "translate(-50%, -50%)",
    left: "50%"
  }
});

/**
 *
 * @param props.fetchVersions {function}
 * @param props.setSelectedVersion {function}
 * @param props.isPreviewFullScreen {boolean}
 * @param props.selectedBase
 * @param props.selectedGame
 * @param props.selectedVersion
 * @param props.deleteVersionFromView
 * @param props.isPreviewLoaded
 * @param props.setIsPreviewLoaded
 * @param props.applyPreview
 * @param props.refresh
 * @param props.userPermissions
 * @param props.setIsDownloadingPlayable
 * @param props.prevButton
 * @param props.setValue
 * @param props.versionRename
 * @param props.versions
 * @param props.saveVersionAndApplyPreview
 * @param props.iFrameId
 * @param props.totalSizeValue
 * @param props.userCompany
 * @param props.setUserCompany
 * @param props.localizationLanguage
 * @param props.setPortrait
 * @param props.classes
 * @param props.setSelectedVersionPathValue
 * @param props.createSnackbar
 * @param props.plans
 * @param props.setSelectedVersionPathValue
 * @param props.getPlanIndex
 * @param props.selectedVersion.gameConfig.components.autoplay1
 * @returns {JSX.Element|null}
 */
export default function containerGamePreview(props) {
  const [deviceId, setDeviceId] = useState(DEFAULT_DEVICE_ID);
  let [isPortrait, setIsPortrait] = useState(true);
  let [gameTitle, setGameTitle] = useState("");
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [protoReloadKey, setProtoReloadKey] = useState(0);
  const [iframeSize, setIframeSize] = useState(null); // {w, h} | null — manual drag-resize
  const [isDragging, setIsDragging] = useState(false);
  const [stageSize, setStageSize] = useState(null); // {width, height} of the stage box
  // ZOOM_FIT (the default) or a number, exactly as the snapshot editor holds it.
  const [zoom, setZoom] = useState(ZOOM_FIT);
  const dragStartRef = useRef(null);
  const stageRef = useRef(null);
  const stageObserverRef = useRef(null);
  // Snapshot editor bases never reach this component (home.js gives them their
  // own preview), so "engine" here means versionedPlayEngine.
  const isPlayEngine = isVersionedPlayEngineBase(props.selectedBase);
  const reloadPlayEngine = () => {
    props.setIsPreviewLoaded(false);
    setPreviewLoaded(false);
    setProtoReloadKey((k) => k + 1);
  };

  // ── Listen for agent-triggered reload requests ────────────────────────────
  useEffect(() => {
    const handler = () => reloadPlayEngine();
    window.addEventListener("playengine:agent-reload", handler);
    return () => window.removeEventListener("playengine:agent-reload", handler);
  });

  // ── PLAYENGINE: listen for playengine:ready then deliver the snapshot ──────
  useEffect(() => {
    if (!isPlayEngine) return;

    const handleMessage = async (event) => {
      if (event.data?.type !== "playengine:ready") return;
      const iframe = document.getElementById(props.iFrameId);
      if (!iframe || event.source !== iframe.contentWindow) return;

      const snapshot = _.get(props, "selectedVersion.playEngineConfig", null);
      if (snapshot) {
        // Resolve @server: references to base64 before sending to playEngine
        const hydrated = await hydrateAssetSources(snapshot, {
          images: _.get(props, "media.images", []),
          atlases: props.atlases || [],
          spines: props.spines || [],
          bitmapFonts: props.bitmapFonts || [],
          sounds: props.sounds || [],
          fonts: props.fonts || [],
          videos: props.videos || [],
        });
        // Re-verify iframe is still available after async work
        const iframeCheck = document.getElementById(props.iFrameId);
        if (!iframeCheck || !iframeCheck.contentWindow) return;
        iframeCheck.contentWindow.postMessage(
          { type: "playengine:load-snapshot", snapshot: hydrated },
          "*"
        );
        console.log("[Dashboard] playengine:load-snapshot sent (hydrated)");
      } else {
        console.warn("[Dashboard] playengine:ready received but no snapshot available");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [props.selectedBase, props.selectedVersion, props.media, props.spines]);

  // ── PLAYENGINE: push new snapshot proactively when version switches ─────────
  useEffect(() => {
    if (!isPlayEngine) return;
    const snapshot = _.get(props, "selectedVersion.playEngineConfig", null);
    if (!snapshot) return;
    const iframe = document.getElementById(props.iFrameId);
    if (!iframe || !iframe.contentWindow) return;

    // Resolve @server: references to base64 before sending to playEngine
    (async () => {
      const hydrated = await hydrateAssetSources(snapshot, {
        images: _.get(props, "media.images", []),
        atlases: props.atlases || [],
        spines: props.spines || [],
        bitmapFonts: props.bitmapFonts || [],
        sounds: props.sounds || [],
        fonts: props.fonts || [],
        videos: props.videos || [],
      });
      const iframeCheck = document.getElementById(props.iFrameId);
      if (!iframeCheck || !iframeCheck.contentWindow) return;
      iframeCheck.contentWindow.postMessage(
        { type: "playengine:load-snapshot", snapshot: hydrated },
        "*"
      );
      console.log("[Dashboard] playengine:load-snapshot sent on version change (hydrated)");
    })();
  }, [_.get(props, "selectedVersion._id"), props.spines]);
  // ── END PLAYENGINE ──────────────────────────────────────────────────────────

  const undoCtx = useContext(UndoContext);
  const handleCtrlZ = () => {
    undo();
  };
  useCtrlZEffect(handleCtrlZ);
  const handleCtrlShiftZ = () => {
    redo();
  };
  useCtrlShiftZEffect(handleCtrlShiftZ);

  const undo = () => {
    let gameConf;
    try {
      console.log(" undoStack  ", JSON.parse(undoCtx.undoStack[undoCtx.cursor - 1]).maxLength != null);
      gameConf = false;
    } catch {
      gameConf = true;
    }
    if (gameConf) {
      let gameConfig = undoCtx.applyUndo(props.selectedVersion.gameConfig, 1);
      if (!gameConfig) {
        return;
      }
      if (typeof gameConfig.components.autoplay1 !== "undefined") {
        gameConfig.components.autoplay1.startAutoplay = props.prevButton;
      }
      applyUpdates(gameConfig);
    } else {
      let recorderSettings = JSON.parse(undoCtx.applyUndo(props.selectedVersion.recorderSettings, 0));
      // if (!recorderSettings) {
      //     return
      // }
      applyUpdatesRecorder(recorderSettings);
    }
  };
  const redo = () => {
    let gameConf;
    try {
      console.log(" redostack  ", JSON.parse(undoCtx.undoStack[undoCtx.cursor + 1]).maxLength != null);
      gameConf = false;
    } catch {
      gameConf = true;
    }
    if (!gameConf) {
      const recorderSettings = JSON.parse(undoCtx.applyRedo());
      // if (!recorderSettings) {
      //     return
      // }
      applyUpdatesRecorder(recorderSettings);
    } else {
      let gameConfig = undoCtx.applyRedo();

      if (!gameConfig) {
        return;
      }
      if (typeof gameConfig.components.autoplay1 !== "undefined") {
        gameConfig.components.autoplay1.startAutoplay = props.prevButton;
      }

      applyUpdates(gameConfig);
    }
  };
  useEffect(() => {
    if (props.selectedVersion) {
      setGameTitle(props.selectedVersion.title);
    }
  }, [props.selectedVersion]);
  useEffect(() => {
    props.fetchVersions(props.selectedBase._id, localStorage.getItem("user.companyID"));
    if (props.selectedVersion) {
      props.setValue(getVersionByID(props.selectedVersion._id));
    }
    setGameTitle(props.versionRename);
  }, [props.versionRename]);

  function getVersionByID(versionIDSString) {
    const found = props.versions.filter((version) => {
      return String(version._id) === versionIDSString;
    });
    if (_.size(found) > 0) {
      return found[0];
    } else {
      //throw { message: "could not find version by id: ", versionIDSString };
      return {};
    }
  }

  const applyUpdates = (gameConfig) => {
    props.applyPreview({
      newGameConfig: gameConfig,
      selectedBase: props.selectedBase
    });
    props.saveVersionAndApplyPreview(
      {
        newSelectedVersion: {
          ...props.selectedVersion,
          gameConfig
        }
      },
      false,
      true
    );
    props.setValue((prev) => {
      return {
        ...prev,
        gameConfig
      };
    });
  };

  const applyUpdatesRecorder = (recorderSettings) => {
    props.saveVersionAndApplyPreview(
      {
        newSelectedVersion: {
          ...props.selectedVersion,
          recorderSettings
        }
      },
      false,
      true
    );
    props.setValue((prev) => {
      return {
        ...prev,
        recorderSettings
      };
    });
  };
  useEffect(() => {
    if (!props.selectedVersion) {
      // console.log("selected version is not found");
      undoCtx.clearUndoStack();

      return;
    }

    undoCtx.clearUndoStack();
  }, [props.selectedVersion?._id]);
  // The frame runs at the picked device's real pixel size — the playable lays
  // itself out for the pixels it is given, so fitting the iframe itself to the
  // pane would preview a device nobody ships on. Fitting is a transform on the
  // wrapper instead, and every size below stays in unscaled device pixels.
  const defaultSize = orientedDeviceSize(devicePresetById(deviceId), isPortrait);
  const displayWidth = iframeSize ? iframeSize.w : defaultSize.width;
  const displayHeight = iframeSize ? iframeSize.h : defaultSize.height;

  // The largest scale that still leaves the whole frame (and its handles) inside
  // the stage. Never above 1:1 — fitting is there to rescue an oversized frame,
  // not to blow a small one up to fill the pane.
  const fitScale = useMemo(() => {
    if (!stageSize || !displayWidth || !displayHeight) return 1;
    const availableWidth = Math.max(1, stageSize.width - STAGE_INSET);
    const availableHeight = Math.max(1, stageSize.height - STAGE_INSET);
    return clampZoom(Math.min(1, availableWidth / displayWidth, availableHeight / displayHeight));
  }, [displayHeight, displayWidth, stageSize]);

  const scale = zoom === ZOOM_FIT ? fitScale : clampZoom(zoom);

  // Floored, not rounded: a box a rounded-up pixel wider than the fit maths
  // allowed for would raise a scrollbar, which takes that pixel back off the
  // stage and starts the fit oscillating.
  const boxWidth = Math.floor(displayWidth * scale);
  const boxHeight = Math.floor(displayHeight * scale);

  // Written onto the handles themselves rather than resolved against the zoom
  // box: Safari does not reliably re-lay-out children of a box that contains a
  // transformed (composited) iframe while that box resizes mid-drag.
  const handleGeometry = {
    right: { left: boxWidth + 6, height: boxHeight },
    bottom: { top: boxHeight + 6, width: boxWidth },
    corner: { left: boxWidth, top: boxHeight },
    overlay: { left: Math.round(boxWidth / 2), top: Math.round(boxHeight / 2) }
  };

  // Fit has to react to the pane getting narrower (the settings panel opening,
  // the window resizing), not just to a new device — so the stage is measured
  // rather than assumed.
  //
  // A callback ref rather than an effect: this component returns early until a
  // base and a version are picked, so the stage mounts some renders after the
  // first one. An effect keyed on anything else would have already run by then
  // and would never observe the node — leaving stageSize null, fit pinned at 1×
  // and the frame overflowing the pane at its full device size.
  const attachStage = useCallback((node) => {
    if (stageObserverRef.current) {
      stageObserverRef.current.disconnect();
      stageObserverRef.current = null;
    }
    stageRef.current = node;
    if (!node) return;

    const measure = () =>
      setStageSize((current) =>
        current && current.width === node.clientWidth && current.height === node.clientHeight
          ? current
          : { width: node.clientWidth, height: node.clientHeight }
      );
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    stageObserverRef.current = observer;
  }, []);

  function trackFrameChange(detail) {
    const event = {
      category: "Preview",
      action: "Orientation Changed",
      label:
        "game: " +
        props.selectedGame?.title +
        ", base: " +
        props.selectedBase?.title +
        ", version: " +
        props.selectedVersion?.title +
        ", " +
        detail,
      dimension1: localStorage.getItem("user.id")
    };
    ReactGA.event(event);
    ReactGA4.event(event);
  }

  // Drop the dragged size, the same way switching orientation does — otherwise
  // picking a device would appear to do nothing. Zoom goes back to Fit with it:
  // a new frame is a new size, and the one it was pinned at is very likely the
  // wrong one for it.
  function selectDevice(nextDeviceId) {
    const preset = devicePresetById(nextDeviceId);
    setIframeSize(null);
    setZoom(ZOOM_FIT);
    setDeviceId(nextDeviceId);
    trackFrameChange("device: " + preset.label + " " + preset.width + "x" + preset.height);
  }

  // A typed size is a dragged size by another route: it is nobody's preset, so
  // it lands in the same place and the toolbar reads it back as "Custom size".
  function applyCustomSize({ width, height }) {
    const portrait = height >= width;
    setIframeSize({ w: width, h: height });
    setZoom(ZOOM_FIT);
    setIsPortrait(portrait);
    props.setPortrait(portrait);
    trackFrameChange("device: custom " + width + "x" + height);
  }

  function toggleOrientation() {
    const portrait = !isPortrait;
    setIframeSize(null);
    setZoom(ZOOM_FIT);
    setIsPortrait(portrait);
    props.setPortrait(portrait);
    trackFrameChange("orientation: " + (portrait ? "portrait" : "landscape"));
  }

  function startDrag(event, edge) {
    event.preventDefault();
    event.stopPropagation();
    const containerDiv = stageRef.current;
    const currentW = displayWidth;
    const currentH = displayHeight;
    // The cap is on the device size, so the on-screen room the stage has
    // converts back through the scale before it becomes a limit.
    const maxW = containerDiv ? Math.max(120, (containerDiv.clientWidth - STAGE_INSET) / scale) : 2048;
    const maxH = containerDiv ? Math.max(120, (containerDiv.clientHeight - STAGE_INSET) / scale) : 2048;
    dragStartRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startW: currentW,
      startH: currentH,
      edge,
      lastW: currentW,
      lastH: currentH,
      maxW,
      maxH,
      scale
    };
    // Dragging under Fit would be a tug of war — every pixel the frame gains,
    // fit takes straight back off the scale and nothing moves. Freezing the zoom
    // at what is already on screen makes the handle do what it looks like it does.
    if (zoom === ZOOM_FIT) setZoom(scale);
    // Capture the pointer for the duration of the drag.
    //
    // What is being resized is an IFRAME, and a pointer that crosses into one
    // stops delivering events to this document — they go to the playable's own
    // document instead. Pull the handle inwards quickly and the cursor overtakes
    // the shrinking frame; the release then lands inside the iframe, this
    // document never sees the pointerup, and the frame keeps following the mouse
    // with no button held. Capture retargets every event to the handle until the
    // release, wherever the cursor happens to be — including outside the window,
    // the other way a plain mouseup goes missing.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (_err) {
      // No capture available: the listeners below still run, they are just
      // interruptible by the iframe again.
    }
    setIsDragging(true);
    setIframeSize({ w: currentW, h: currentH });
    document.body.style.cursor = edge === "right" ? "ew-resize" : edge === "bottom" ? "ns-resize" : "nwse-resize";
    document.body.style.userSelect = "none";

    // Captured events are retargeted to the handle but still bubble to the
    // document, so these keep working either way.
    function onPointerMove(moveEvent) {
      const {
        startX,
        startY,
        startW,
        startH,
        edge: dragEdge,
        maxW: mxW,
        maxH: mxH,
        scale: dragScale
      } = dragStartRef.current;
      // The pointer moves in screen pixels; the frame is measured in device
      // pixels, and at 0.5x one of those is half a screen pixel.
      const dx = (moveEvent.clientX - startX) / dragScale;
      const dy = (moveEvent.clientY - startY) / dragScale;
      let newW = startW;
      let newH = startH;
      // The wrapper is centered (justifyContent/alignItems: center), so each edge
      // only moves half the total size change. Multiply by 2 so the edge tracks 1:1 with the mouse.
      if (dragEdge === "right" || dragEdge === "corner")
        newW = Math.min(mxW, Math.max(120, Math.round(startW + 2 * dx)));
      if (dragEdge === "bottom" || dragEdge === "corner")
        newH = Math.min(mxH, Math.max(120, Math.round(startH + 2 * dy)));
      dragStartRef.current.lastW = newW;
      dragStartRef.current.lastH = newH;
      setIframeSize({ w: newW, h: newH });
    }

    // pointercancel too: the browser can take the pointer away mid-drag (a
    // touch turning into a scroll, the window losing it), and that has to end
    // the drag for the same reason a missing pointerup must not.
    function onPointerUp() {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setIsDragging(false);
      const finalW = dragStartRef.current.lastW;
      const finalH = dragStartRef.current.lastH;
      const portrait = finalH >= finalW;
      // The dragged size stays: it is no longer any device's preset, so keeping
      // it is the only honest thing to show until one is picked again.
      setIframeSize({ w: finalW, h: finalH });
      setIsPortrait(portrait);
      props.setPortrait(portrait);
    }

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);
  }

  const classes = useClass();

  if (_.isNil(props.selectedBase) || _.isNil(props.selectedVersion)) {
    return null;
  }
  // A legacy base without a built package, or an engine base on a server that
  // has no engine bundle mounted — either way there is nothing to load.
  const previewSrc = previewSourceFor(props.selectedBase, previewsGenerated);
  if (!previewSrc) {
    return <div>Game preview is not available</div>;
  }
  return (
    <div id={"gamePreviewDiv"} className={classes.gamePreviewDiv}>
      <GamePreviewToolbar
        iFrameId={props.iFrameId}
        totalSizeValue={props.totalSizeValue}
        selectedVersion={props.selectedVersion}
        selectedBase={props.selectedBase}
        setSelectedBase={props.setSelectedBase}
        predefinedGameComponents={props.predefinedGameComponents}
        selectedGame={props.selectedGame}
        userCompany={props.userCompany}
        applyPreview={props.applyPreview}
        setUserCompany={props.setUserCompany}
        versionsAreFetched={props.versionsAreFetched}
        deleteVersionFromView={props.deleteVersionFromView}
        fetchVersions={props.fetchVersions}
        versionUpdate={props.versionUpdate}
        isPortrait={isPortrait}
        versionRename={props.versionRename}
        versions={props.versions}
        localizationLanguage={props.localizationLanguage}
        devices={DEVICE_PRESETS}
        deviceId={deviceId}
        isCustomSize={Boolean(iframeSize)}
        previewWidth={displayWidth}
        previewHeight={displayHeight}
        zoom={zoom}
        zoomSteps={ZOOM_STEPS}
        effectiveZoom={scale}
        onSelectZoom={setZoom}
        onSelectDevice={selectDevice}
        onCustomSize={applyCustomSize}
        onToggleOrientation={toggleOrientation}
        setSelectedVersion={props.setSelectedVersion}
        isPreviewLoaded={props.isPreviewLoaded}
        setIsPreviewLoaded={props.setIsPreviewLoaded}
        classes={props.classes}
        setSelectedVersionPathValue={props.setSelectedVersionPathValue}
        refresh={props.refresh}
        onPlayEngineRefresh={reloadPlayEngine}
        createSnackbar={props.createSnackbar}
        plans={props.plans}
        getPlanIndex={props.getPlanIndex}
        userPermissions={props.userPermissions}
        setIsDownloadingPlayable={props.setIsDownloadingPlayable}
        setValue={props.setValue}
        versionHierarchy={props.versionHierarchy}
      ></GamePreviewToolbar>
      {(!props.isPreviewLoaded || !previewLoaded) && (
        <div className={classes.gamePreviewLoader}>
          <CircularProgress />
        </div>
      )}
      <div id={"iframeDiv"} className={classes.iframeDiv} ref={attachStage}>
        <div className={classes.zoomBox} style={{ width: boxWidth, height: boxHeight }}>
          <div
            className={classes.iframeWrapper}
            style={{ width: displayWidth, height: displayHeight, transform: `scale(${scale})` }}
          >
            <iframe
              allow="autoplay"
              id={props.iFrameId}
              key={isPlayEngine ? `proto-${protoReloadKey}` : undefined}
              allow-scripts={"true"}
              className={classes.gamePreviewIframe + " " + (props.isPreviewFullScreen ? classes.fullScreen : "")}
              src={previewSrc}
              width={displayWidth}
              height={displayHeight}
              style={{
                visibility: "hidden",
                ...(isDragging ? { pointerEvents: "none" } : {})
              }}
              onLoad={() => {
                props.setIsPreviewLoaded(true);
                setPreviewLoaded(true);
                if (isPlayEngine) {
                  // PlayEngine: the primary snapshot delivery happens via playengine:ready → handleMessage.
                  // As a safety net, yield to the event loop first (so playengine:ready can be
                  // processed if it's already queued), then send the snapshot again. If the
                  // engine already received it, the duplicate is silently ignored by connect().
                  // This prevents the engine's waitForSnapshot from timing out in edge cases.
                  const snapshot = _.get(props, "selectedVersion.playEngineConfig", null);
                  if (snapshot) {
                    setTimeout(async () => {
                      const hydrated = await hydrateAssetSources(snapshot, {
                        images: _.get(props, "media.images", []),
                        atlases: props.atlases || [],
                        spines: props.spines || [],
                        bitmapFonts: props.bitmapFonts || [],
                        sounds: props.sounds || [],
                        fonts: props.fonts || [],
                        videos: props.videos || [],
                      });
                      const iframeCheck = document.getElementById(props.iFrameId);
                      if (!iframeCheck || !iframeCheck.contentWindow) return;
                      iframeCheck.contentWindow.postMessage(
                        { type: "playengine:load-snapshot", snapshot: hydrated },
                        "*"
                      );
                    }, 0);
                  }
                } else {
                  props.applyPreview({
                    newGameConfig: props.selectedVersion.gameConfig,
                    selectedBase: props.selectedBase
                  });
                }
                document.getElementById("gamePreviewIframe").style.setProperty("visibility", "visible");
              }}
            ></iframe>
          </div>
          {!props.isPreviewFullScreen && (
            <>
              <div
                className={classes.resizeHandleRight}
                style={handleGeometry.right}
                onPointerDown={(e) => startDrag(e, "right")}
              >
                <div />
              </div>
              <div
                className={classes.resizeHandleBottom}
                style={handleGeometry.bottom}
                onPointerDown={(e) => startDrag(e, "bottom")}
              >
                <div />
              </div>
              <div
                className={classes.resizeHandleCorner}
                style={handleGeometry.corner}
                onPointerDown={(e) => startDrag(e, "corner")}
              >
                <svg viewBox="0 0 10 10" width="10" height="10" style={{ display: "block", pointerEvents: "none" }}>
                  <line x1="1" y1="9" x2="9" y2="1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="5" y1="9" x2="9" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              {isDragging && iframeSize && (
                <div className={classes.dragSizeOverlay} style={handleGeometry.overlay}>
                  {iframeSize.w} × {iframeSize.h}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div
        id={"buttons"}
        style={{ right: "0px", display: undoCtx.clearButtons() ? undoCtx.clearButtons() : "none" }}
        className={classes.buttons}
      >
        <button
          style={{ background: "none", border: "none", borderRadius: "5px", width: "50px", height: "50px" }}
          onClick={undo}
        >
          <svg height="60%" viewBox="0 0 24 24" width="60%" xmlns="http://www.w3.org/2000/svg">
            <path
              fill={undoCtx.buttonUndoColor}
              d="M9 10h6c1.654 0 3 1.346 3 3s-1.346 3-3 3h-3v2h3c2.757 0 5-2.243 5-5s-2.243-5-5-5H9V5L4 9l5 4v-3z"
            ></path>
          </svg>
        </button>

        <button
          style={{ background: "none", border: "none", borderRadius: "5px", width: "50px", height: "50px" }}
          onClick={redo}
        >
          <svg height="60%" viewBox="0 0 24 24" width="60%" xmlns="http://www.w3.org/2000/svg">
            <path
              fill={undoCtx.buttonRedoColor}
              d="M9 18h3v-2H9c-1.654 0-3-1.346-3-3s1.346-3 3-3h6v3l5-4-5-4v3H9c-2.757 0-5 2.243-5 5s2.243 5 5 5z"
            ></path>
          </svg>
        </button>
      </div>
      <div
        style={{ wordBreak: "break-all", flexShrink: "1", overflow: "hidden", marginLeft: "5px", marginRight: "5px" }}
      >
        <Typography id={"selectedVersionDiv"} className={classes.selectedVersionTitle} variant={"h6"}>
          {gameTitle}
        </Typography>
      </div>
    </div>
  );
}
